// js/core/field-guards.js — the input guards a save runs in the browser BEFORE
// it writes, online or offline. Pure: dates in, messages out. One place, so the
// field app and the admin refuse the same things with the same words
// (stranger sweep, 2026-10-17: a production report dated in the future, a
// timesheet row with 40h and no employee, a cert that expires before it was
// issued — none of them were refused anywhere).
import { t } from './i18n.js';

const pad = n => String(n).padStart(2, '0');
const num = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

/** Date → 'YYYY-MM-DD' in the LOCAL calendar (never toISOString: after ~5 pm
 *  Pacific that is tomorrow). */
export function localDate(d = new Date()) {
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}
export function todayLocal() { return localDate(new Date()); }

export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** The seven ISO dates (Mon..Sun) of the week that holds dateIso. */
export function weekDatesFor(dateIso) {
  const [y, m, d] = String(dateIso || todayLocal()).split('-').map(Number);
  const start = new Date(y, (m || 1) - 1, d || 1);
  const dow = start.getDay();
  start.setDate(start.getDate() + (dow === 0 ? -6 : 1 - dow));
  return DAY_KEYS.map((_, i) => {
    const x = new Date(start); x.setDate(start.getDate() + i); return localDate(x);
  });
}

/** null when dateIso is today or earlier; the refusal otherwise. */
export function futureDateMsg(dateIso, today = todayLocal()) {
  if (!dateIso) return t('guard.dateMissing');
  return dateIso > today ? t('guard.futureDate', { today }) : null;
}

/** issued + validity months → local ISO (same month overflow as setMonth). */
export function addMonthsIso(issuedIso, months) {
  const [y, m, d] = String(issuedIso || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  return localDate(new Date(y, m - 1 + (Number(months) || 0), d));
}

/** The one rule for every certificate saver (field + the three admin forms). */
export function certDateError(issued, expires, today = todayLocal()) {
  if (!issued) return t('guard.certIssuedMissing');
  if (issued > today) return t('guard.certIssuedFuture', { today });
  if (!expires) return t('guard.certExpiryMissing');
  if (expires < issued) return t('guard.certExpiryBeforeIssued');
  return null;
}

/** 'Week Of' / 'Week Ending' typed outside the week the Date places. */
export function weekMismatchMsg(label, typedIso, weekDates) {
  if (!typedIso || !weekDates || weekDates.length < 7) return null;
  if (typedIso >= weekDates[0] && typedIso <= weekDates[6]) return null;
  return t('guard.weekMismatch', { label, typed: typedIso, from: weekDates[0], to: weekDates[6] });
}

/**
 * Weekly Timesheet rows. errors = refusals (no employee on a row with hours,
 * negative hours, more than 24h in a day). future = cells on days after
 * today — a warning, not a refusal: crews fill the week on Friday.
 * opts (time clock): openCells [{ name, day }] = a shift still checked in, a
 * refusal each; clockedMissing [names] = clocked on the job but not on the
 * sheet, one question in clocked (the foreman may file without them).
 */
export function validateTimesheetWeek(employees, weekDates, today = todayLocal(), opts = {}) {
  const errors = [], future = [];
  (employees || []).forEach((emp, i) => {
    const days = emp?.days || {};
    const name = (emp?.name || '').trim() || t('guard.rowN', { n: i + 1 });
    const filled = DAY_KEYS.some(k => { const c = days[k] || {}; return !!c.sick || num(c.r) !== 0 || num(c.ot) !== 0; });
    if (!filled) return;
    if (!(emp?.worker_id || '').trim()) { errors.push(t('guard.tsNoEmployee', { n: i + 1 })); return; }
    DAY_KEYS.forEach((k, idx) => {
      const c = days[k] || {};
      const r = num(c.r), ot = num(c.ot), day = DAY_NAMES[idx];
      if (r < 0 || ot < 0) errors.push(t('guard.tsNegative', { name, day }));
      else if (r + ot > 24) errors.push(t('guard.tsOver24', { name, day, h: r + ot }));
      if ((c.sick || r > 0 || ot > 0) && weekDates[idx] > today) future.push(`${name} — ${day} ${weekDates[idx]}`);
    });
  });
  (opts.openCells || []).forEach(({ name, day }) => errors.push(t('guard.tsOpenPunch', { name, day })));
  const clocked = opts.clockedMissing?.length ? [t('guard.tsAddClockedWorkers', { list: opts.clockedMissing.join(', ') })] : [];
  return { errors, future, clocked };
}

/** Extra Work Order lines: a row with hours needs a worker, a day has 24h at
 *  most, and the order needs at least one labour or material line. */
export function validateEwoLines(labour, materials) {
  const errors = [];
  let any = false;
  (labour || []).forEach((r, i) => {
    const reg = num(r?.reg_hours), ot = num(r?.ot_hours), td = num(r?.td_hours);
    const hrs = reg + ot + td;
    const who = r?.worker_id || (r?.worker_name || '').trim();
    if (hrs === 0 && !who) return;                  // an untouched row
    if (hrs > 0) any = true;                         // there IS work on the order, whoever did it
    if (!who) { errors.push(t('guard.ewoNoWorker', { n: i + 1 })); return; }
    const name = (r?.worker_name || '').trim() || t('guard.rowN', { n: i + 1 });
    if (reg < 0 || ot < 0 || td < 0) errors.push(t('guard.ewoNegative', { name }));
    else if (hrs > 24) errors.push(t('guard.ewoOver24', { name, h: hrs }));
  });
  (materials || []).forEach(m => {
    if (num(m?.qty) > 0 || (!m?.from_list && (m?.description || '').trim())) any = true;
  });
  if (!any) errors.push(t('guard.ewoNoLines'));
  return errors;
}

/** A request that failed for lack of signal: say so and keep the screen. */
export function networkErrorMessage(err, online = (typeof navigator !== 'undefined' ? navigator.onLine : true)) {
  const m = String((err && err.message) || err || '');
  if (online === false || /failed to fetch|networkerror|load failed|network request failed/i.test(m)) {
    return t('guard.noSignal');
  }
  return null;
}
